'use strict';

import _ from 'lodash';
import {ApplicationProcessModes, ProductIds, FormKeys} from '../constants/ApplicationConstants';
import Products from './Products';
import DateTime from './DateTime';
import ApplicationHelper from './ApplicationHelper';
import {FundingTypes, ContributionValues as PaymentMethods} from '../constants/FundingYourProductConstants';
import {PensionTypeBenefits} from '../constants/PensionTypeConstants';
import {bankAccountText} from '../constants/IncomeDistributionConstants';
import {YesNoValues} from '../constants/ApplicationConstants';

const PersonalInjuryCGTOrDownsizer = [FundingTypes.Personal_injury_payment, FundingTypes.CGT_contribution, FundingTypes.Downsizer_contribution];
const SpecificPensionTypes = [PensionTypeBenefits.ENTRY_DISABILITY_TOTAL_PERMANENT, PensionTypeBenefits.ENTRY_TERMINAL_ILLNESS, PensionTypeBenefits.ENTRY_DEATH];
const ageUnder = (data, maxAge) => {
    const dateOfBirth = ApplicationHelper.getClientFieldValue(data.applicationData, 'dateOfBirth');
    if (!dateOfBirth) {
        return false;
    }
    const age = DateTime.getAge(new Date(), dateOfBirth);
    return age < maxAge;
};
const IsExistingInvestor = () => window.NOA.userContext.isInvestor && window.NOA.userContext.isExisting;
const IsNewInvestor = () => window.NOA.userContext.isInvestor && !window.NOA.userContext.isExisting;
const ContainsPersonalInjuryCGTOrDownsizer = (applicationData) => {
    if (!applicationData || !applicationData.form || !applicationData.form.fundingYourProduct || !applicationData.form.fundingYourProduct.fundingSources) {
        return false;
    }

    const fundingType = _.find(applicationData.form.fundingYourProduct.fundingSources, (value) => PersonalInjuryCGTOrDownsizer.indexOf(value.rollOverType) != -1);
    return !!fundingType;
};
const ContainsSpecificPensionBenefit = (applicationData) => {
    if (!applicationData || !applicationData.form || !applicationData.form.pensionType) {
        return false;
    }

    return SpecificPensionTypes.indexOf(applicationData.form.pensionType.selectedBenefitItem) != -1;
};
// Scenario 1
const IsSuperAndAgeUnder16 = (data) => Products.isSuper(data.applicationData.productId) ? ageUnder(data, 16) : false;
// Scenario 3
const IsFeesExcessive = (data) => (Products.isSuper(data.applicationData.productId) || Products.isPension(data.applicationData.productId)) &&
    window.NOA.userContext.isAdviser &&
    data.applicationData.form.adviceFee &&
    data.applicationData.form.adviceFee.IsFeesExcessive === true;
// Scenario 5
const IsAnnonymousPublicInvestor = (data) => (Products.isInvestment(data.applicationData.productId) ||
        Products.isPension(data.applicationData.productId)) && IsNewInvestor();
// Scenario 6
const IsNonPersonalInvestment = (data) => {
    switch (data.formKey) {
        case FormKeys.COMPANY_INVESTMENT:
        case FormKeys.PARTNERSHIP_INVESTMENT:
        case FormKeys.SMSF_INVESTMENT:
        case FormKeys.TRUST_INVESTMENT:
            return true;
        default:
            return false;
    }
};
// Scenario 7
const IsKycNotCheckedForPensionAndPersonalInvestments = (data) => {
    if (!IsExistingInvestor()) {
        return false;
    }

    const kycFlag = ApplicationHelper.getClientKycFlag(data.applicationData);
    switch (data.formKey) {
        case ProductIds.PENSION:
        case FormKeys.INDIVIDUAL_INVESTMENT:
        case FormKeys.JOINT_INVESTMENT:
        case FormKeys.SOLE_TRADER_INVESTMENT:
            return !kycFlag;
        default:
            return false;
    }
};
// Scenario 11
const IsPersonalInjuryCGTOrDownsizerContribution = (data) => (Products.isSuper(data.applicationData.productId) ||
        Products.isPension(data.applicationData.productId)) &&
    ContainsPersonalInjuryCGTOrDownsizer(data.applicationData);
// Scenario 13
const IsSpecificPensionBenefit = (data) => Products.isPension(data.applicationData.productId) &&
    ContainsSpecificPensionBenefit(data.applicationData);
// Scenario 14
const IsPowerOfAttorney = (data) => (Products.isSuper(data.applicationData.productId) ||
        Products.isPension(data.applicationData.productId) ||
        Products.isInvestment(data.applicationData.productId)) &&
    !!ApplicationHelper.getClientFieldValue(data.applicationData, 'isPowerOfAttorney', false);
// Scenario 18
const IsBackdoor = data => !!(data && window.NOA && window.NOA.isBackdoorMode);

const ContainsBlankBankAccount = (data) => {
    if (!data) {
        return false;
    }
    return !data.accountNumber || !data.branchNumber || !data.accountHolderName;
};

// Scenario 19 Funding bank account is blank
const IsFundingBankAccountBlank = (data) => {
    if (!data || !data.applicationData || !data.applicationData.form || !data.applicationData.form.fundingYourProduct || !data.applicationData.form.fundingYourProduct.fundingSources) {
        return false;
    }

    const directDebit = _.find(data.applicationData.form.fundingYourProduct.fundingSources, (value) => value.paymentMethod === PaymentMethods.DIRECT_D);
    if (directDebit) {
        const result = ContainsBlankBankAccount(directDebit);
        console.log('IsFundingBankAccountBlank - ' + result);
        return result;
    }
    return false;
}
// Scenario 20 rip bank account is blank
const ContainsBlankRipBankAccount = (applicationData) => {
    if (!applicationData || !applicationData.form || !applicationData.form.regularInvestmentPlan) {
        return false;
    }
    const rip = applicationData.form.regularInvestmentPlan;
    if (rip.ripOption === YesNoValues.YES) {
        const result = !rip.bankAccount || !rip.branchNumber || !rip.nameOfAccountHolder;
        console.log('ContainsBlankRipBankAccount - ' + result);
        return result;    
    }
    return false;
};

const IsRipBankAccountBlank = (data) => (Products.isInvestment(data.applicationData.productId) || Products.isSuper(data.applicationData.productId)) && ContainsBlankRipBankAccount(data.applicationData);
// Scenario 21 pension payment bank account is blank
const ContainsBlankPensionBankAccount =  (applicationData) => {
    if (!applicationData || !applicationData.form || !applicationData.form.pensionPayment) {
        return false;
    }
    const pp = applicationData.form.pensionPayment;

    const result = ContainsBlankBankAccount(pp);
    console.log('ContainsBlankPensionBankAccount - ' + result);
    return result;
};

const IsPensionPaymentBankAccountBlank = (data) => Products.isPension(data.applicationData.productId) && ContainsBlankPensionBankAccount(data.applicationData);
// Scenario 22 income distribution bank account is blank
const ContainsBlankIncomeDistributionBankAccount = (applicationData) => {
    if (!applicationData || !applicationData.form || !applicationData.form.incomeDistribution) {
        return false;
    }

    const id = applicationData.form.incomeDistribution;
    if (id.paymentMethod === bankAccountText) {
        const result = ContainsBlankBankAccount(id);
        console.log('ContainsBlankIncomeDistributionBankAccount - ' + result);
        return result;
    }
    return false;
};
const IsIncomeDistributionBankAccountBlank = (data) => Products.isInvestment && ContainsBlankIncomeDistributionBankAccount(data.applicationData);

const rules = [
    IsBackdoor,
    IsAnnonymousPublicInvestor,
    IsNonPersonalInvestment,
    IsSuperAndAgeUnder16,
    IsFeesExcessive,
    IsKycNotCheckedForPensionAndPersonalInvestments,
    IsPersonalInjuryCGTOrDownsizerContribution,
    IsSpecificPensionBenefit,
    IsPowerOfAttorney,
    IsFundingBankAccountBlank,
    IsRipBankAccountBlank,
    IsPensionPaymentBankAccountBlank,
    IsIncomeDistributionBankAccountBlank
];

class ApplicationModeProcessorClass {
    constructor() {
    }

    getProcessModeByAppData(applicationData) {
        // Responsible for deciding if shell account is to be created (a.k.a if the process mode is OFFLINE) 
        // based on the application form input data alone, e.g. CGT funding
        // ref: path 3 criteria in confluence page https://confluence.prod.cba/display/FAM/Create+shell+account
        if (!applicationData || !applicationData.form) {
            return ApplicationProcessModes.ONLINE;
        }

        const formKey = Products.getFormKey(applicationData.form.accountType, applicationData.productId);
        const data = {
            applicationData,
            formKey
        };

        let mode = ApplicationProcessModes.ONLINE;
        _.forEach(rules, (rule) => {
            if (rule(data)) {
                mode = ApplicationProcessModes.OFFLINE;
                return false;
            }
        });
        return mode;
    }
}

const ApplicationModeProcessor = new ApplicationModeProcessorClass();
export default ApplicationModeProcessor;


// WEBPACK FOOTER //
// C:/GH/1106/advice.noa.origination/~/eslint-loader!./utils/ApplicationModeProcessor.js